import clsx from 'clsx'
import React, { useState, useRef, useEffect, ReactNode, Children } from 'react'

import styles from './AutoComplete.module.scss'

// ---------------------------
// TYPES
// ---------------------------
export interface AutocompleteChangeEventDetail<T = any> {
  value: T[]
}

export interface AutocompleteCustomEvent<T = any> {
  detail: AutocompleteChangeEventDetail<T>
}

export interface AutocompleteSearchCustomEvent {
  detail: string
}

type AutocompleteType = 'default' | 'extended'

// ---------------------------
// PROPS
// ---------------------------
interface Props<T = any> {
  value?: T[]
  onChange?: (event: AutocompleteCustomEvent<T>) => void
  onSearchValueChange?: (event: AutocompleteSearchCustomEvent) => void
  onSearchChange?: (value: string) => void

  placeholder?: string
  labelConfig?: { text: string }

  multiple?: boolean
  required?: boolean
  loading?: boolean

  type?: AutocompleteType

  // identify an option/value (defaults to `.id`, falls back to JSON.stringify)
  getOptionId?: (val: any) => string | number

  className?: string
  children: ReactNode
}

// ---------------------------
// COMPONENT
// ---------------------------
export const AutoComplete = <T,>({
  value = [],
  onChange,
  onSearchValueChange,
  onSearchChange,
  placeholder,
  labelConfig,
  multiple = true,
  loading,
  type = 'default',
  getOptionId,
  className,
  children,
}: Props<T>) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // ---------------------------
  // Outside click closes dropdown
  // ---------------------------
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const options = Children.toArray(children).filter((child: any) => child?.props?.slot !== 'selected-values')
  const selectedValues = value || []

  // ---------------------------
  // Stable identity comparison
  // ---------------------------
  const resolveId = (val: any): string | number => {
    if (getOptionId) return getOptionId(val)
    if (val && typeof val === 'object' && 'id' in val) return val.id
    return JSON.stringify(val)
  }

  const isSelected = (val: any) => {
    const targetId = resolveId(val)
    return selectedValues.some((v: any) => resolveId(v) === targetId)
  }

  const handleSelect = (val: any) => {
    let newValues: any[] = []
    const targetId = resolveId(val)

    if (isSelected(val)) {
      newValues = selectedValues.filter((v: any) => resolveId(v) !== targetId)
    } else {
      newValues = multiple ? [...selectedValues, val] : [val]
    }

    onChange?.({ detail: { value: newValues } })

    // reset search text so the list goes back to "browse all" for the next pick,
    // but keep the dropdown open for multi-select
    setSearch('')
    onSearchValueChange?.({ detail: '' })
    onSearchChange?.('')

    setOpen(false)
  }

  const handleRemove = (val: any) => {
    const targetId = resolveId(val)
    const newValues = selectedValues.filter((v: any) => resolveId(v) !== targetId)
    onChange?.({ detail: { value: newValues } })
  }

  // ---------------------------
  // Extract label safely
  // ---------------------------
  const getLabel = (child: any) => {
    if (!child?.props?.children) return child?.props?.label || ''

    const labelChild = Children.toArray(child.props.children).find(
      (c: any) => React.isValidElement(c) && (c as any).props?.slot === 'label',
    )

    if (React.isValidElement(labelChild)) {
      const content = (labelChild.props as any).children
      return typeof content === 'string' ? content : child.props?.label || ''
    }

    return child.props?.label || ''
  }

  const selectedValuesSlot = Children.toArray(children).find((child: any) => child?.props?.slot === 'selected-values')

  // ---------------------------
  // Highlight matched substring
  // ---------------------------
  const highlightMatch = (label: string, query: string) => {
    if (!query) return label
    const idx = label.toLowerCase().indexOf(query.toLowerCase())
    if (idx === -1) return label
    return (
      <>
        {label.slice(0, idx)}
        <strong>{label.slice(idx, idx + query.length)}</strong>
        {label.slice(idx + query.length)}
      </>
    )
  }

  // ---------------------------
  // HANDLE SEARCH
  // ---------------------------
  const handleSearch = (val: string) => {
    setSearch(val)
    setOpen(true)
    onSearchValueChange?.({ detail: val })
    onSearchChange?.(val)
  }

  const showInlineCount = !search && selectedValues.length > 0

  return (
    <div ref={containerRef} className={clsx(styles.wrapper, className)}>
      {labelConfig?.text && <div className={styles.label}>{labelConfig.text}</div>}

      {/* SEARCH INPUT */}
      <div className={styles.inputRow}>
        <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
          <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <input
          ref={inputRef}
          className={styles.input}
          placeholder={showInlineCount ? `${selectedValues.length} selected` : placeholder}
          value={search}
          onFocus={() => setOpen(true)}
          onChange={e => handleSearch(e.target.value)}
        />
        {search && (
          <button type="button" className={styles.clearIcon} onClick={() => handleSearch('')} aria-label="Clear search">
            ×
          </button>
        )}
      </div>

      {/* SELECTED PILLS — always visible, stacked below the input */}
      {type === 'extended' && selectedValuesSlot
        ? selectedValuesSlot
        : selectedValues.length > 0 && (
            <div className={styles.pillsList}>
              {selectedValues.map((val: any, i) => (
                <div key={resolveId(val) ?? i} className={styles.pillRow}>
                  <span>{getLabel({ props: { value: val, label: (val as any)?.label } })}</span>
                  <button
                    type="button"
                    className={styles.pillRemove}
                    onClick={() => handleRemove(val)}
                    aria-label="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

      {/* DROPDOWN */}
      {open && (
        <div className={styles.dropdown}>
          {loading && <div className={styles.loading}>Loading...</div>}

          <div className={styles.options}>
            {options.map((child: any, index) => {
              const val = child.props.value
              const label = getLabel(child)
              const checked = isSelected(val)

              if (search && !label?.toLowerCase().includes(search.toLowerCase())) {
                return null
              }

              return (
                <div
                  key={resolveId(val) ?? index}
                  className={clsx(styles.option, { [styles.selected]: checked })}
                  onClick={() => handleSelect(val)}
                >
                  {React.isValidElement(child)
                    ? React.cloneElement(child as any, {
                        children: React.Children.map((child as any).props.children, (c: any) =>
                          React.isValidElement(c) && (c as any).props?.slot === 'label'
                            ? React.cloneElement(c, {}, highlightMatch(label, search))
                            : c,
                        ),
                      })
                    : child}
                  {multiple && checked && <span className={styles.checkMark}>✓</span>}
                </div>
              )
            })}

            {!loading &&
              options.every((child: any) => {
                const label = getLabel(child)
                return search && !label?.toLowerCase().includes(search.toLowerCase())
              }) && <div className={styles.empty}>No results found</div>}
          </div>
        </div>
      )}
    </div>
  )
}
